import { PERMISSIONS, type PermissionCode, type PermissionDefinition } from '@loot/shared';

/**
 * Organização das permissões no assistente de perfis (no estilo do Bankline Itaú): departamento
 * do menu (ADR 0012) → grande processo → funcionalidade (a permissão). Só muda a apresentação:
 * o perfil continua gravando a lista de permissões. Cada permissão aparece em um lugar só; um
 * teste confere que toda permissão nova do catálogo entrou aqui.
 *
 * O nome do departamento vem de `areas.<id>`; o do processo, de `roles.wizard.processes.<id>`.
 */
export interface PermissionProcess {
  id: string;
  permissions: readonly PermissionCode[];
}

export interface PermissionArea {
  id: string;
  processes: readonly PermissionProcess[];
}

export const PERMISSION_AREAS: readonly PermissionArea[] = [
  // Decidir aprovações vale para todos os departamentos (a tela de Aprovações é uma só).
  {
    id: 'approvals',
    processes: [{ id: 'approvalDecisions', permissions: ['approval.request.decide'] }],
  },
  {
    id: 'executive',
    processes: [{ id: 'executiveDashboard', permissions: ['executive.dashboard.view'] }],
  },
  {
    id: 'purchasing',
    processes: [
      {
        id: 'purchaseRequests',
        permissions: ['purchasing.request.create', 'purchasing.request.manage'],
      },
      { id: 'purchaseReceiving', permissions: ['purchasing.receipt.register'] },
      { id: 'purchaseSettings', permissions: ['purchasing.quote_rule.manage'] },
    ],
  },
  {
    id: 'facilities',
    processes: [
      {
        id: 'facilitiesRequests',
        permissions: [
          'facilities.request.create',
          'facilities.request.read',
          'facilities.request.manage',
          'facilities.request.supervise',
        ],
      },
      {
        id: 'maintenance',
        permissions: [
          'facilities.work_order.read',
          'facilities.work_order.manage',
          'facilities.work_order.accept',
          'facilities.maintenance_plan.manage',
          'facilities.checklist.manage',
          'facilities.pmoc.manage',
          'facilities.pmoc.validate',
        ],
      },
      {
        id: 'assets',
        permissions: [
          'asset.asset.read',
          'asset.facilities.manage',
          'asset.inventory.manage',
          'asset.accounting.manage',
          'asset.import',
          'asset.class.manage',
          'asset.policy.manage',
        ],
      },
      { id: 'facilitiesMaterials', permissions: ['facilities.material.manage'] },
      { id: 'facilitiesServices', permissions: ['facilities.services.manage'] },
      {
        id: 'travelPolicy',
        permissions: [
          'facilities.travel_policy.read',
          'facilities.travel_policy.manage',
          'facilities.travel_policy.approve',
        ],
      },
      { id: 'facilitiesManagement', permissions: ['facilities.catalog.manage'] },
    ],
  },
  {
    id: 'legal',
    processes: [
      {
        id: 'legalRequests',
        permissions: [
          'legal.request.create',
          'legal.request.read',
          'legal.request.manage',
          'legal.request.assign',
          'legal.request.approve',
        ],
      },
      {
        id: 'legalConfidentiality',
        permissions: [
          'legal.restricted.read',
          'legal.confidential.read',
          'legal.compartment.labor',
          'legal.compartment.personal_data',
          'legal.compartment.litigation',
          'legal.compartment.corporate',
        ],
      },
      {
        id: 'legalLibrary',
        permissions: ['legal.template.read', 'legal.template.manage', 'legal.template.approve'],
      },
      { id: 'legalManagement', permissions: ['legal.catalog.manage'] },
    ],
  },
  {
    id: 'hr',
    processes: [{ id: 'internalCommunication', permissions: ['intranet.content.manage'] }],
  },
  {
    id: 'it',
    processes: [
      {
        id: 'itSoftware',
        permissions: [
          'it.catalog.read',
          'it.catalog.manage',
          'it.subscription.read',
          'it.subscription.manage',
          'it.license.assign',
          'it.license.import',
        ],
      },
      {
        id: 'itAccess',
        permissions: ['it.access.read', 'it.access.manage'],
      },
      {
        id: 'itGovernance',
        permissions: ['it.governance.read', 'it.governance.manage', 'it.incident.manage'],
      },
      {
        id: 'itProjects',
        permissions: ['it.project.read', 'it.project.manage'],
      },
      {
        id: 'itFinance',
        permissions: [
          'it.finance.read',
          'it.service.manage',
          'it.invoice.manage',
          'it.allocation.manage',
          'it.period.close',
          'it.invoice.confirm_payment',
        ],
      },
      {
        id: 'itDevices',
        permissions: [
          'it.device.read',
          'it.device.manage',
          'it.device.import',
          'it.repair.cost.read',
          'asset.it.manage',
        ],
      },
      { id: 'itServiceDesk', permissions: ['it.ticket.work', 'it.ticket.manage'] },
      { id: 'itSecurity', permissions: ['it.ticket.security'] },
    ],
  },
  {
    id: 'finance',
    processes: [
      {
        id: 'treasuryAccounts',
        permissions: [
          'finance.bank_account.read',
          'finance.bank_account.manage',
          'finance.bank_account.approve',
          'finance.bank_account.reveal',
        ],
      },
      {
        id: 'payables',
        permissions: [
          'finance.payable.read',
          'finance.payable.manage',
          'finance.payable.intake',
          'finance.payable.schedule',
          'finance.payment.register',
        ],
      },
      {
        id: 'treasuryCash',
        permissions: [
          'finance.treasury.read',
          'finance.treasury.movement.manage',
          'finance.treasury.adjustment.approve',
          'finance.treasury.settings.manage',
          'finance.treasury.reconcile',
          'finance.cashflow.forecast.manage',
          'finance.cashflow.scenario.manage',
        ],
      },
    ],
  },
  {
    id: 'fpa',
    processes: [
      {
        id: 'fpaPlanning',
        permissions: [
          'fpa.data.read',
          'fpa.plan.edit',
          'fpa.plan.manage',
          'fpa.plan.approve',
          'fpa.actual.manage',
          'fpa.actual.audit',
          'fpa.actual.origin.manage',
        ],
      },
      { id: 'fpaCycle', permissions: ['fpa.settings.manage', 'fpa.period.close'] },
      {
        id: 'fpaAccess',
        permissions: ['fpa.access.manage', 'fpa.payroll.read', 'fpa.payroll.manage'],
      },
    ],
  },
  {
    id: 'help',
    processes: [
      {
        id: 'knowledgeCenter',
        permissions: [
          'knowledge.document.read',
          'knowledge.document.drafts',
          'knowledge.document.review',
          'knowledge.document.approve',
          'knowledge.document.publish',
          'knowledge.library.manage',
        ],
      },
      {
        id: 'knowledgeRestricted',
        permissions: [
          'knowledge.restricted.finance',
          'knowledge.restricted.legal',
          'knowledge.restricted.hr',
          'knowledge.restricted.personnel',
        ],
      },
      { id: 'helpArticles', permissions: ['help.article.manage'] },
    ],
  },
  {
    id: 'system',
    processes: [
      {
        id: 'corporateStructure',
        permissions: [
          'core.legal_entity.read',
          'core.legal_entity.manage',
          'core.branch.read',
          'core.branch.manage',
          'core.location.manage',
          'core.department.manage',
          'core.cost_center.read',
          'core.cost_center.manage',
        ],
      },
      {
        id: 'representatives',
        permissions: [
          'core.representative.read',
          'core.representative.manage',
          'core.representative.validate',
          'core.representative.read_sensitive',
        ],
      },
      {
        id: 'peopleOrganization',
        permissions: [
          'people.org.read',
          'people.person.manage',
          'people.private.read',
          'people.private.manage',
          'people.mailing.read',
          'people.preferences.manage',
          'people.access.grant',
          'people.payroll_cost.manage',
          'people.hr_request.work',
          'people.training.manage',
          'people.acknowledgement.read',
          'people.position_request.manage',
          'people.employee.manage',
          'people.allocation.manage',
        ],
      },
      {
        id: 'jobsCareers',
        permissions: ['career.catalog.manage', 'career.salary.read', 'career.salary.manage'],
      },
      {
        id: 'financialStructure',
        permissions: [
          'core.currency.read',
          'core.currency.manage',
          'structure.catalog.read',
          'structure.catalog.create',
          'structure.catalog.update',
          'structure.catalog.status',
          'structure.catalog.admin',
        ],
      },
      {
        id: 'partners',
        permissions: [
          'partner.partner.read',
          'partner.partner.manage',
          'partner.partner.approve',
          'partner.restriction.commercial',
          'partner.restriction.financial',
          'partner.restriction.legal',
          'partner.bank.read',
          'partner.bank.reveal',
          'partner.bank.manage',
          'partner.bank.approve',
          'partner.payment_terms.manage',
          'partner.document_type.manage',
          'partner.document.review_commercial',
          'partner.document.review_financial',
          'partner.document.review_legal',
          'partner.document.restricted',
          'partner.portal.read',
          'partner.portal.invite',
          'partner.portal.revoke',
        ],
      },
      {
        id: 'dataImport',
        permissions: [
          'masterdata.import.read',
          'masterdata.import.export',
          'masterdata.import.validate',
          'masterdata.import.apply',
          'masterdata.import.restricted',
          'masterdata.import.salary',
        ],
      },
      { id: 'governance', permissions: ['approval.governance.manage'] },
      {
        id: 'serviceLevels',
        permissions: [
          'core.process.manage',
          'core.calendar.manage',
          'sla.policy.propose',
          'sla.report.read',
          'sla.report.export',
          'sla.audit.read',
          'sla.revision.request',
          'sla.exception.record',
        ],
      },
      {
        id: 'access',
        permissions: [
          'iam.user.read',
          'iam.user.manage',
          'iam.access.grant',
          'iam.role.read',
          'iam.role.manage',
        ],
      },
      {
        id: 'platformControl',
        permissions: ['audit.log.read', 'audit.access.read', 'feedback.report.manage'],
      },
    ],
  },
];

const DEFINITIONS = new Map<string, PermissionDefinition>(PERMISSIONS.map((p) => [p.code, p]));

export function permissionDefinition(code: string): PermissionDefinition | undefined {
  return DEFINITIONS.get(code);
}

/** Todas as permissões de um departamento ou de um processo. */
export function codesOf(item: PermissionArea | PermissionProcess): PermissionCode[] {
  return 'processes' in item ? item.processes.flatMap((p) => p.permissions) : [...item.permissions];
}

/** Departamentos que têm alguma das permissões (para abrir um perfil já gravado). */
export function areasWith(permissions: Iterable<string>): string[] {
  const held = new Set(permissions);
  return PERMISSION_AREAS.filter((a) => codesOf(a).some((c) => held.has(c))).map((a) => a.id);
}

/** Só as permissões que estão nos departamentos escolhidos. */
export function withinAreas(permissions: Iterable<string>, areaIds: Iterable<string>): Set<string> {
  const allowed = new Set(
    PERMISSION_AREAS.filter((a) => new Set(areaIds).has(a.id)).flatMap(codesOf) as string[],
  );
  return new Set([...permissions].filter((c) => allowed.has(c)));
}

/**
 * Tipos de perfil: só modelos que pré-marcam as funcionalidades nos departamentos escolhidos.
 * O tipo não é gravado e não limita nada; a lista final dos tipos ainda será confirmada pela
 * LOOT. O Administrador Master não entra aqui: é um perfil do sistema.
 */
export const PROFILE_TEMPLATES = [
  'custom',
  'generalOperator',
  'backofficeOperator',
  'approver',
  'departmentManager',
] as const;
export type ProfileTemplate = (typeof PROFILE_TEMPLATES)[number];

/** Abrir pedidos e chamados e acompanhar os próprios; consultar a Central de Conhecimento. */
const REQUESTER: readonly PermissionCode[] = [
  'purchasing.request.create',
  'facilities.request.create',
  'legal.request.create',
  'knowledge.document.read',
];

/** Decisões de aprovação e aceite de cada departamento. */
const APPROVER: readonly PermissionCode[] = [
  'approval.request.decide',
  'legal.request.approve',
  'legal.template.approve',
  'fpa.plan.approve',
  'knowledge.document.approve',
  'facilities.work_order.accept',
];

/** Cadastros básicos que quem aprova consulta para decidir. */
const APPROVER_READS: readonly PermissionCode[] = [
  'core.legal_entity.read',
  'core.branch.read',
  'core.cost_center.read',
  'people.org.read',
  'partner.partner.read',
];

/** Nunca vêm pré-marcadas para o gestor: administrar acessos, auditoria e conta bancária completa. */
const MANAGER_EXCLUDED: readonly PermissionCode[] = [
  'iam.user.read',
  'iam.user.manage',
  'iam.access.grant',
  'iam.role.read',
  'iam.role.manage',
  'audit.log.read',
  'audit.access.read',
  'partner.bank.reveal',
];

const has = (list: readonly PermissionCode[], p: PermissionDefinition) =>
  list.includes(p.code as PermissionCode);

const TEMPLATE_RULES: Record<ProfileTemplate, (p: PermissionDefinition) => boolean> = {
  custom: () => false,
  generalOperator: (p) => has(REQUESTER, p),
  // Dia a dia: sem as sensíveis, as expressas e as de administração do grupo todo.
  backofficeOperator: (p) => !p.sensitive && !p.explicitOnly && !p.groupOnly,
  approver: (p) => has(REQUESTER, p) || has(APPROVER, p) || has(APPROVER_READS, p),
  departmentManager: (p) => (!p.explicitOnly || has(APPROVER, p)) && !has(MANAGER_EXCLUDED, p),
};

/** Funcionalidades que o modelo pré-marca nos departamentos escolhidos. */
export function templatePermissions(
  template: ProfileTemplate,
  areaIds: Iterable<string>,
): Set<string> {
  const rule = TEMPLATE_RULES[template];
  const chosen = new Set(areaIds);
  return new Set(
    PERMISSION_AREAS.filter((a) => chosen.has(a.id))
      .flatMap(codesOf)
      .filter((code) => {
        const def = DEFINITIONS.get(code);
        return def !== undefined && rule(def);
      }),
  );
}

/** Código do perfil sugerido a partir do nome: "Operador BackOffice" → OPERADOR-BACKOFFICE. */
export function roleCodeFromName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 30)
    .replace(/-+$/, '');
}
